// Portfolio Administration v1.0 PR 3 §7.3 + Loan record foundations — one
// Parties-tab card. Renders the server-resolved counterparty name (linked to
// its CRM record), role, effective contact info with an override indicator,
// key-person / primary-contact chips, and — for former parties — a muted
// presentation with the effective date range. Edit opens the right-side
// drawer (full hydration; see EditPartyDrawer).

import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useInactivateLoanParty } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { EditPartyDrawer } from "./EditPartyDrawer";
import { calendarDate } from "../common/colorBand";
import { displayEmail, isPlaceholderEmail, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";
import type { LoanPartyAssignmentDto } from "@/api/portfolioAdmin/types";

interface PartyRowProps {
  loanId: string;
  party: LoanPartyAssignmentDto;
  readOnly?: boolean;
}

/** CRM detail route per counterparty type; null = no destination page
 *  (OtherContact, PhoenixUser — staff live in the 7-pill widget). */
export function counterpartyHref(type: string, id: string): string | null {
  switch (type) {
    case "Person":
      return `/counterparties/persons/${id}`;
    case "LegalEntity":
      return `/counterparties/entities/${id}`;
    case "Business":
      return `/counterparties/businesses/${id}`;
    case "Broker":
      return `/brokers/${id}`;
    case "Vendor":
      return `/portfolio-admin/vendors/${id}`;
    case "LocalEnforcer":
      return `/portfolio-admin/local-enforcers/${id}`;
    default:
      return null;
  }
}

export function PartyRow({ loanId, party, readOnly = false }: PartyRowProps) {
  const toast = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const inactivate = useInactivateLoanParty(loanId);

  const onRemove = async () => {
    try {
      await inactivate.mutateAsync(party.id);
      toast.success("Party removed (kept as a former party).");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not remove party.",
      );
    }
  };

  const former = !party.isActive;
  const name =
    party.counterpartyName ??
    `${party.counterpartyType} · ${party.counterpartyId.slice(0, 8)}…`;
  const href = counterpartyHref(party.counterpartyType, party.counterpartyId);
  const hasOverride =
    !!party.emailOverride || !!party.phoneOverride || !!party.addressOverride;
  const dateRange = former
    ? `${party.effectiveFrom ? calendarDate(party.effectiveFrom) : "—"} – ${party.effectiveTo ? calendarDate(party.effectiveTo) : "—"}`
    : null;

  return (
    <div
      className={`flex items-start justify-between gap-space-3 border border-border-light rounded-md px-space-3 py-space-2 ${former ? "bg-white-warm opacity-70" : "bg-white-pure"}`}
      data-testid={`party-row-${party.id}`}
      data-former={former || undefined}
    >
      <div className="flex flex-1 flex-col min-w-0 gap-[2px]">
        <div className="flex items-center gap-space-2 flex-wrap">
          {href ? (
            <Link
              to={href}
              className="font-display font-semibold text-small text-gold-shadow hover:underline truncate"
              data-testid="party-name-link"
            >
              {name}
            </Link>
          ) : (
            <span className="font-display font-semibold text-small text-onyx truncate">
              {name}
            </span>
          )}
          {party.isKeyPerson && (
            <Badge status="draft" data-testid="party-key-person-chip">
              Key person
            </Badge>
          )}
          {party.isPrimaryContact && (
            <Badge status="draft" data-testid="party-primary-contact-chip">
              Primary contact
            </Badge>
          )}
          {hasOverride && (
            <Badge status="info" data-testid="party-override-chip">
              Loan-scoped override
            </Badge>
          )}
        </div>
        <span className="text-caption text-gray-mid">
          {party.roleOnLoan}
          {dateRange && ` · ${dateRange}`}
        </span>
        {(party.email || party.phone) && (
          <span className="text-caption text-gray-mid truncate" data-testid="party-contact">
            {[
              isPlaceholderEmail(party.email)
                ? NO_EMAIL_ON_FILE
                : displayEmail(party.email),
              party.phone,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        )}
        {party.notes && (
          <span className="text-caption text-gray-mid truncate">{party.notes}</span>
        )}
      </div>
      {!readOnly && (
        <div className="flex items-center gap-space-2 shrink-0">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setEditOpen(true)}
            data-testid="party-edit"
          >
            Edit
          </Button>
          {party.isActive && (
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => void onRemove()}
              disabled={inactivate.isPending}
              data-testid="party-inactivate"
            >
              Remove
            </Button>
          )}
        </div>
      )}
      {editOpen && (
        <EditPartyDrawer
          loanId={loanId}
          party={party}
          open={editOpen}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  );
}
